import { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
  useTheme
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

function GamePerformanceTable({
  data,
  loading,
  title = 'Game Performance',
  showIntegration = false,
  onToggleIntegration
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const divider = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const panelHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#fafafa';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.05)' : '#f4f5f7';
  const tableHeaderColor = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const primaryText = isDark ? theme.palette.text.primary : '#0f172a';
  const secondaryText = isDark ? theme.palette.text.secondary : '#6b7280';
  const rowAlt = isDark ? 'rgba(255,255,255,0.02)' : '#fafafa';
  const rowTop = isDark ? 'rgba(226,0,27,0.06)' : 'rgba(226,0,27,0.025)';
  const rowHover = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)';
  const rowBorder = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const rankTopColor = '#e2001b';
  const rankColor = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)';
  const barTrack = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const footerBg = isDark ? 'rgba(255,255,255,0.02)' : '#fafafa';
  const footerText = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.35)';

  const sortedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return [...data]
      .filter((game) => game.roundsPlayed > 0 || game.totalBets > 0)
      .sort((a, b) => (b.totalBets || 0) - (a.totalBets || 0));
  }, [data]);

  const formatNumber = (num, decimals = 0) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined) return '-';
    return `${Number(num).toFixed(2)}%`;
  };

  const getRtpColor = (actual, theoretical) => {
    if (actual === null || actual === undefined || theoretical === null || theoretical === undefined) return 'default';
    const diff = actual - theoretical;
    if (diff > 2) return 'error';
    if (diff < -2) return 'success';
    return 'warning';
  };

  if (loading) {
    return (
      <Card>
        <MDBox p={3}>
          <Skeleton variant='text' width={200} height={32} />
          <Skeleton variant='rectangular' height={300} sx={{ mt: 2 }} />
        </MDBox>
      </Card>
    );
  }

  if (!data || sortedData.length === 0) {
    return (
      <Card>
        <MDBox p={3}>
          <MDTypography variant='h6' fontWeight='medium'>
            {title}
          </MDTypography>
          <MDBox
            display='flex'
            flexDirection='column'
            justifyContent='center'
            alignItems='center'
            py={4}
            sx={{ backgroundColor: 'grey.50', borderRadius: 2, mt: 2 }}
          >
            <MDTypography variant='body2' color='text' fontWeight='medium'>
              No Game Data
            </MDTypography>
            <MDTypography variant='caption' color='text' mt={1}>
              No games with activity found for the selected period and filters
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    );
  }

  const maxBets = Math.max(...sortedData.map((g) => g.totalBets || 0), 1);

  return (
    <Card
      sx={{
        borderRadius: '14px',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Panel header */}
      <MDBox
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.75,
          borderBottom: `1px solid ${divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          background: panelHeaderBg
        }}
      >
        <MDBox display='flex' alignItems='center' gap={1.5}>
          <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: primaryText }}>
            {title}
          </MDTypography>
          <MDBox
            sx={{
              px: 1.25,
              py: 0.35,
              borderRadius: '6px',
              background: isDark ? 'rgba(255,255,255,0.05)' : theme.palette.background.paper,
              border: `1px solid ${divider}`
            }}
          >
            <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.65rem', color: tableHeaderColor, letterSpacing: '0.04em' }}>
              {sortedData.length} games
            </MDTypography>
          </MDBox>
        </MDBox>
        <FormControlLabel
          control={
            <Switch
              checked={showIntegration}
              onChange={(e) => onToggleIntegration && onToggleIntegration(e.target.checked)}
              size='small'
            />
          }
          label={
            <MDTypography variant='caption' fontWeight='medium' sx={{ fontSize: '0.72rem' }}>
              Show Integration
            </MDTypography>
          }
          sx={{ mr: 0 }}
        />
      </MDBox>

      <TableContainer
        sx={{
          maxHeight: 460,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { height: '4px', width: '4px' },
          '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255,255,255,0.15)' : theme.palette.action.hover, borderRadius: '2px' }
        }}
      >
        <Table size='small' stickyHeader sx={{ width: '100%', minWidth: { xs: 420, sm: 'auto' } }}>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  background: tableHeaderBg,
                  color: tableHeaderColor,
                  fontWeight: 700,
                  fontSize: '0.67rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  padding: { xs: '9px 10px', sm: '9px 16px' },
                  borderBottom: `1px solid ${divider}`,
                  whiteSpace: 'nowrap',
                  boxShadow: 'none'
                }
              }}
            >
              <TableCell sx={{ width: 28, pr: 0 }}>#</TableCell>
              <TableCell>Game</TableCell>
              {showIntegration && <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Integration</TableCell>}
              <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Rounds</TableCell>
              <TableCell align='right' sx={{ display: { xs: 'none', md: 'table-cell' } }}>Players</TableCell>
              <TableCell align='right' sx={{ minWidth: 120 }}>Bets</TableCell>
              <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Wins</TableCell>
              <TableCell align='right'>GGR</TableCell>
              <TableCell align='right'>RTP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((game, index) => {
              const isTop = index === 0;
              const sharePct = Math.round(((game.totalBets || 0) / maxBets) * 100);
              const ggrPositive = (game.ggr || 0) >= 0;
              return (
                <TableRow
                  key={`${game.gameId || game.name}-${index}`}
                  sx={{
                    background: isTop ? rowTop : index % 2 === 0 ? 'transparent' : rowAlt,
                    '@media (hover: hover)': { '&:hover': { background: rowHover } },
                    '& td': {
                      padding: { xs: '9px 10px', sm: '9px 16px' },
                      borderBottom: `1px solid ${rowBorder}`
                    },
                    '&:last-child td': { borderBottom: 'none' }
                  }}
                >
                  {/* Rank */}
                  <TableCell sx={{ width: 28, pr: 0 }}>
                    <MDTypography variant='caption' sx={{ fontSize: '0.65rem', color: isTop ? rankTopColor : rankColor, fontWeight: isTop ? 700 : 400 }}>
                      {index + 1}
                    </MDTypography>
                  </TableCell>

                  {/* Game name */}
                  <TableCell>
                    <MDBox display='flex' alignItems='center' gap={0.75}>
                      <MDTypography
                        variant='button'
                        fontWeight={isTop ? 'bold' : 'medium'}
                        sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: primaryText, lineHeight: 1.3 }}
                      >
                        {game.name || 'Unknown'}
                      </MDTypography>
                      {game.integrationCount && game.integrationCount > 1 && (
                        <Tooltip title={`Aggregated from ${game.integrationCount} integrations`} arrow>
                          <Chip
                            label={`×${game.integrationCount}`}
                            size='small'
                            variant='outlined'
                            sx={{ height: 16, fontSize: '0.6rem', minWidth: 28, borderColor: divider, color: secondaryText }}
                          />
                        </Tooltip>
                      )}
                    </MDBox>
                  </TableCell>

                  {showIntegration && (
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: secondaryText }}>{game.clientName || '-'}</MDTypography>
                    </TableCell>
                  )}

                  <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: secondaryText }}>{formatNumber(game.roundsPlayed)}</MDTypography>
                  </TableCell>

                  <TableCell align='right' sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: secondaryText }}>{formatNumber(game.uniquePlayers)}</MDTypography>
                  </TableCell>

                  {/* Bets with inline share bar */}
                  <TableCell align='right' sx={{ minWidth: 120 }}>
                    <MDBox display='flex' flexDirection='column' alignItems='flex-end' gap={0.4}>
                      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.78rem', color: primaryText }}>
                        {formatNumber(game.totalBets, 2)}
                      </MDTypography>
                      <MDBox sx={{ width: '100%', height: '3px', borderRadius: '2px', background: barTrack, overflow: 'hidden' }}>
                        <MDBox
                          sx={{
                            height: '100%',
                            width: `${sharePct}%`,
                            background: isTop ? '#e2001b' : 'rgba(226,0,27,0.45)',
                            borderRadius: '2px',
                            transition: 'width 0.4s ease'
                          }}
                        />
                      </MDBox>
                    </MDBox>
                  </TableCell>

                  <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: secondaryText }}>{formatNumber(game.totalWins, 2)}</MDTypography>
                  </TableCell>

                  {/* GGR with color */}
                  <TableCell align='right'>
                    <MDTypography
                      variant='caption'
                      fontWeight='medium'
                      sx={{ fontSize: '0.75rem', color: ggrPositive ? '#1fa678' : '#db3f57' }}
                    >
                      {formatNumber(game.ggr, 2)}
                    </MDTypography>
                  </TableCell>

                  {/* RTP */}
                  <TableCell align='right'>
                    {game.gameCategory === 'pvp' ? (
                      <Tooltip title='RTP not applicable for PvP' arrow>
                        <MDTypography variant='caption' sx={{ fontSize: '0.7rem', color: secondaryText, fontStyle: 'italic' }}>N/A</MDTypography>
                      </Tooltip>
                    ) : game.actualRtp !== null && game.actualRtp !== undefined ? (
                      <Chip
                        label={formatPercent(game.actualRtp)}
                        size='small'
                        color={getRtpColor(game.actualRtp, game.theoreticalRtp)}
                        sx={{ minWidth: 54, fontSize: '0.67rem', height: 19, fontWeight: 600 }}
                      />
                    ) : (
                      <MDTypography variant='caption' sx={{ color: secondaryText }}>—</MDTypography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer */}
      <MDBox
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.25,
          borderTop: `1px solid ${divider}`,
          background: footerBg
        }}
      >
        <MDTypography variant='caption' sx={{ fontSize: '0.68rem', color: footerText }}>
          Sorted by total bets · Bar shows share of top game · GGR coloured green/red
        </MDTypography>
      </MDBox>
    </Card>
  );
}

GamePerformanceTable.defaultProps = {
  data: null,
  loading: false,
  title: 'Game Performance',
  showIntegration: false,
  onToggleIntegration: null
};

GamePerformanceTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      gameId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      gameIds: PropTypes.arrayOf(PropTypes.number),
      integrationCount: PropTypes.number,
      clientName: PropTypes.string,
      name: PropTypes.string,
      gameType: PropTypes.string,
      gameCategory: PropTypes.string,
      roundsPlayed: PropTypes.number,
      uniquePlayers: PropTypes.number,
      totalBets: PropTypes.number,
      totalWins: PropTypes.number,
      ggr: PropTypes.number,
      actualRtp: PropTypes.number,
      theoreticalRtp: PropTypes.number
    })
  ),
  loading: PropTypes.bool,
  title: PropTypes.string,
  showIntegration: PropTypes.bool,
  onToggleIntegration: PropTypes.func
};

export default GamePerformanceTable;
